import { SetterOrUpdater, useRecoilState } from 'recoil';
import { RECIPES, SOURCES } from 'constants/kpiBuilderCatalogue';
import { builderAnswersState } from 'states';
import { gapAOptions, gapBOptions, recipeSources } from 'helpers/kpiBuilder/lookups';
import { chooseProgress, chooseReady, chooseRecipe, chooseSource, chooseSourceB } from 'helpers/kpiBuilder/transitions';
import { PROGRESS_KINDS } from 'helpers/kpiBuilder/progressRecipes';
import { READY_KINDS } from 'helpers/kpiBuilder/readyRecipes';
import { BuilderAnswers, ProgressKind, ReadyKind, RecipeId, SourceId } from 'helpers/kpiBuilder/types';
import { BuilderSection } from './BuilderSection';
import { CustomSourceFields } from './CustomSourceFields';
import { CardOption, OptionCards } from './OptionCards';

const sourceCards = (ids: SourceId[]): CardOption[] => ids.map((id) => ({ id, label: SOURCES[id].label, desc: SOURCES[id].desc }));

const recipeCards: CardOption[] = RECIPES.map((r) => ({
  id: r.id,
  label: r.label,
  desc: `Answers: ${r.answers} You get: ${r.youGet} For example: ${r.example}${r.ready ? '' : ' (Coming later)'}`,
  disabled: !r.ready,
}));

interface SourcesProps {
  a: BuilderAnswers;
  setA: SetterOrUpdater<BuilderAnswers>;
}

// Questions that read one data source.
const OneSource = ({ a, setA }: SourcesProps) => (
  <>
    <BuilderSection title="Which data answers it?" hint="Each source is a different list of records. Pick the one that holds the event you are counting (signing up, joining a course, achieving a level, rating a course).">
      <OptionCards cards={sourceCards(recipeSources(a))} selected={a.src ? [a.src] : []} onSelect={(id) => setA(chooseSource(a, id as SourceId))} />
    </BuilderSection>
    {a.src === 'custom' && <CustomSourceFields />}
  </>
);

// "In one list but not another" reads two.
const TwoSources = ({ a, setA }: SourcesProps) => (
  <>
    <BuilderSection title="Start with everyone who is…" hint="The full list you begin with, e.g. registered.">
      <OptionCards cards={sourceCards(gapAOptions())} selected={a.src ? [a.src] : []} onSelect={(id) => setA(chooseSource(a, id as SourceId))} />
    </BuilderSection>
    <BuilderSection title="…then remove anyone who is" hint="Anyone on this second list is taken out. What is left are the learners who have not reached this step yet.">
      <OptionCards cards={sourceCards(gapBOptions(a))} selected={a.srcB ? [a.srcB] : []} onSelect={(id) => setA(chooseSourceB(a, id as SourceId))} />
    </BuilderSection>
  </>
);

const progressCards: CardOption[] = (Object.keys(PROGRESS_KINDS) as ProgressKind[]).map((id) => ({ id, label: PROGRESS_KINDS[id].label, desc: PROGRESS_KINDS[id].desc }));

// A learning-progress comparison fixes its own data, so only the comparison is chosen.
const Progress = ({ a, setA }: SourcesProps) => (
  <BuilderSection title="Which comparison?" hint="Each one compares two steps of the learning journey. The data for both steps is chosen for you; pick the learners below to narrow them, for example one cadre.">
    <OptionCards cards={progressCards} selected={a.src ? [a.progressKind] : []} onSelect={(id) => setA(chooseProgress(a, id as ProgressKind))} />
  </BuilderSection>
);

const readyCards: CardOption[] = (Object.keys(READY_KINDS) as ReadyKind[]).map((id) => ({ id, label: READY_KINDS[id].label, desc: READY_KINDS[id].desc }));

// A ready-made chart fixes its own data, so only the chart is chosen.
const Ready = ({ a, setA }: SourcesProps) => (
  <BuilderSection title="Which chart?" hint="The data for each chart is chosen for you. Pick the learners below to narrow them, for example one cadre, and change the options that follow.">
    <OptionCards cards={readyCards} selected={a.src ? [a.readyKind] : []} onSelect={(id) => setA(chooseReady(a, id as ReadyKind))} />
  </BuilderSection>
);

// Step 1: what the admin wants to know. Changing it resets the steps after it.
export const QuestionStep = () => {
  const [a, setA] = useRecoilState(builderAnswersState);
  return (
    <BuilderSection title="What do you want to know?" hint="Start with the kind of answer you need. Your choice decides which data, details and charts come next. Changing it later resets the steps below.">
      <OptionCards cards={recipeCards} selected={a.recipe ? [a.recipe] : []} onSelect={(id) => setA(chooseRecipe(a, id as RecipeId))} />
    </BuilderSection>
  );
};

/** Questions that choose their own data or build their own query have no Data step. */
export const hasDataStep = (a: BuilderAnswers): boolean => !!a.recipe && !['funnel', 'template', 'advanced'].includes(a.recipe);

// Step 2: which data answers the question (or which comparison or chart, for the ones that fix their data).
export const DataSourceStep = () => {
  const [a, setA] = useRecoilState(builderAnswersState);
  const needsSource = !!a.recipe && !['gap', 'funnel', 'template', 'progress', 'ready', 'advanced'].includes(a.recipe);

  return (
    <>
      {needsSource && <OneSource a={a} setA={setA} />}
      {a.recipe === 'gap' && <TwoSources a={a} setA={setA} />}
      {a.recipe === 'progress' && <Progress a={a} setA={setA} />}
      {a.recipe === 'ready' && <Ready a={a} setA={setA} />}
    </>
  );
};
